import { useAuth0 } from "@auth0/auth0-react";
import { ArrowBack, CompareArrows, ExitToApp, Person, Search } from "@mui/icons-material";
import { Avatar, Box, Divider, InputBase, ListItemIcon, MenuItem, Paper } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import { selectBaseClient } from "../../Helpers/clientSelection";
import { profileColor } from "../../Helpers/ProfileColor";
import { useNotification } from "../../State/Hooks/useNotificationHooks";
import { selectAllClients } from "../../State/Slices/AllClientsSlice";
import { selectClientValue } from "../../State/Slices/SelectedClientSlice";
import { selectUserInformation } from "../../State/Slices/UserInformationSlice";
import { AbacFeaturesIndicator } from "../ABAC/AttributeProtectedComponent";
import "./Animations.css";

function ProfileDropdown() {
  const useStyles = makeStyles((theme) => ({
    root: {
      position: "absolute",
      top: "2vh",
      right: "2%",
      zIndex: "5",
    },
    menuContainer: {
      position: "absolute",
      top: "0px",
      right: "0",
      width: 280,
      display: "flex",
      flexDirection: "column",
      padding: 2,
      backgroundColor: "transparent",
      overflow: "hidden",
      transition: "height 500ms ease",
      [theme.breakpoints.down("md")]: {
        width: 200,
        top: -18,
        right: -6,
      },
    },
    subContainer: {
      position: "absolute",
      top: "70px",
      right: "0",
      width: 280,
      display: "flex",
      flexDirection: "column",
      padding: 2,
      backgroundColor: "transparent",
      overflow: "hidden",
      transition: "height 500ms ease",
      [theme.breakpoints.down("md")]: {
        width: 200,
        top: 50,
        right: -6,
      },
    },
    paperStyle: {
      [theme.breakpoints.down("md")]: {
        overflowY: "scroll",
      },
      width: "100%",
      overflow: "visible",
      borderRadius: 0,
    },
    paperStyleScroll: {
      width: "100%",
      overflowY: "scroll",
      minHeight: "200px",
      maxHeight: "500px",
      borderRadius: 0,
    },
    mainText: {
      fontSize: "14px",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      height: "70px",
    },
    subMainText: {
      display: "flex",
      flexDirection: "column",
      paddingLeft: "20px",
    },
    boldName: {
      fontWeight: "bold",
      backgroundColor: "rgba(0,0,0, .1)",
      minHeight: "50px",
    },
    profileButton: {
      display: "flex",
      alignItems: "center",
      textAlign: "center",
      transition: ".2s",
      "&:hover": {
        background: "rgba(0,0,0, .1)",
      },
    },
    icon: {
      minWidth: "30px",
    },
    searchBox: {
      width: "100%",
      minHeight: "50px",
      paddingLeft: "5px",
      backgroundColor: "rgba(0,0,0,.1)",
      "&:hover": {
        backgroundColor: "rgba(0,0,0,.2)",
      },
    },
    searchBoxContainer: {
      display: "flex",
      justifyContent: "center",
    },
    menuItem: {
      minHeight: "50px",
    },
  }));

  const classes = useStyles();
  const dispatch = useDispatch();
  const { displayWarning } = useNotification();
  const user = useSelector(selectUserInformation);
  const baseClients = useSelector(selectAllClients);
  const selectedClient = useSelector(selectClientValue);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [activeMenu, setActiveMenu] = useState("main");
  const containerRef = useRef(null);
  const username = user?.nickname ?? "";
  const { logout } = useAuth0();

  const [searchVal, setSearchVal] = useState("");

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Switch tenant: select the base client (and its careers client when it has
  // one). Shares the resilient helper with the login bootstrap so a failed
  // careers lookup can't leave the app without a selected client.
  const selectClient = (baseClient) => {
    selectBaseClient(baseClient, dispatch, {
      onCareersError: () =>
        displayWarning(
          "We couldn't load careers data for this client. Some features may be unavailable."
        ),
    });
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setActiveMenu("main");
        setSearchVal("");
        handleClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [containerRef]);

  return (
    <div className={classes.root}>
      <Box className={classes.menuContainer} ref={containerRef}>
        <Paper className={classes.paperStyle}>
          <MenuItem className={classes.mainText} onClick={handleClick}>
            <Avatar
              style={{
                width: 40,
                height: 40,
                backgroundColor: profileColor(username),
              }}
            >
              {username.charAt(0)}
            </Avatar>
            <div className={classes.subMainText}>
              <span style={{ display: "flex", alignItems: "center", gap: "0.5ch" }}>
                {username} <AbacFeaturesIndicator />
              </span>
              <span style={{ fontWeight: "bold" }}>{selectedClient.clientName}</span>
            </div>
          </MenuItem>
        </Paper>
      </Box>
      {open ? (
        <Box className={classes.subContainer} ref={containerRef}>
          <CSSTransition
            in={activeMenu === "main"}
            timeout={500}
            classNames="menu-primary"
            unmountOnExit
          >
            <Paper className={classes.paperStyle}>
              <Divider />
              <Link to="/profile" style={{ all: "unset" }}>
                <MenuItem className={classes.menuItem}>
                  <ListItemIcon size="large" className={classes.icon}>
                    <Person fontSize="small" />
                  </ListItemIcon>
                  Profile
                </MenuItem>
              </Link>
              {baseClients.length > 1 ? (
                <MenuItem onClick={() => setActiveMenu("clients")} className={classes.menuItem}>
                  <ListItemIcon size="large" className={classes.icon}>
                    <CompareArrows fontSize="small" />
                  </ListItemIcon>
                  Switch Tenant
                </MenuItem>
              ) : null}
              <Divider style={{ marginBottom: "10px" }} />
              <MenuItem
                onClick={() => logout({ returnTo: window.location.origin })}
                className={classes.menuItem}
              >
                <ListItemIcon size="large" className={classes.icon}>
                  <ExitToApp fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Paper>
          </CSSTransition>
          <CSSTransition
            in={activeMenu === "clients"}
            timeout={500}
            classNames="menu-secondary"
            unmountOnExit
          >
            <Paper className={classes.paperStyleScroll}>
              <Divider />
              <MenuItem onClick={() => setActiveMenu("main")}>
                <ListItemIcon size="large">
                  <ArrowBack fontSize="small" />
                </ListItemIcon>
              </MenuItem>
              <Divider />
              <div className={classes.searchBoxContainer}>
                <InputBase
                  className={classes.searchBox}
                  placeholder="Search Tenant..."
                  variant="standard"
                  fullWidth
                  value={searchVal}
                  onChange={(e) => setSearchVal(e.target.value)}
                  endAdornment={<Search style={{ opacity: ".5" }} />}
                />
              </div>
              <Divider />
              {baseClients
                .filter(
                  (baseClient) =>
                    baseClient.clientName.toUpperCase().includes(searchVal.toUpperCase()) ||
                    searchVal === ""
                )
                .map((baseClient, index) => {
                  return (
                    <MenuItem
                      onClick={() => selectClient(baseClient)}
                      key={index}
                      className={
                        selectedClient.id === baseClient.id ? classes.boldName : classes.menuItem
                      }
                    >
                      {baseClient.clientName}
                    </MenuItem>
                  );
                })}
            </Paper>
          </CSSTransition>
        </Box>
      ) : null}
    </div>
  );
}

export default ProfileDropdown;
